import { useState } from "react";
import { Row, Form } from "react-bootstrap";
import { Button } from "components/core/basic/Button";
import { Label } from "../programs/fields";
import { useMutation } from "@tanstack/react-query";
import Loading from "components/core/blocks/Loading";
import Select from "react-select";
import { css } from "../../../../styled-system/css";
import { successToast, errorToast } from "../../../utils/toasts";
import { Center, VStack, Wrap } from "../../../../styled-system/jsx";
import { Modal } from "../../core/basic/RACModal";
import { apiClient } from "../../../zodios/client";
import { TextField } from "../../fields/base/TextField";
import { TextAreaField } from "../../fields/base/TextAreaField";

const DEFAULT_FORM_DATA = {
  name: "",
  prompt: "",
  theme_list: [{ value: "no_theme", label: "No theme" }],
  additional_context: "",
};

interface Props {
  asin: string;
  productId: string;
  overrideModalButtonText?: string | null;
  initialFormData?: typeof DEFAULT_FORM_DATA | null;
}

const ImageGenerationDialogue = ({
  asin,
  productId,
  overrideModalButtonText = null,
  initialFormData = null,
}: Props) => {
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState(initialFormData ? initialFormData : DEFAULT_FORM_DATA);
  const [themeSearch, setThemeSearch] = useState("");

  const generateParametersMutation = useMutation({
    mutationFn: async (vars: { product: string; additional_context?: string }) => {
      return await apiClient.generateGenAiParameters(vars);
    },

    onSuccess: (res) => {
      const { prompt, theme_list } = res;
      const allThemeOptions = theme_list.themes.map((theme) => ({
        label: theme.themeForDisplay,
        value: theme.themeId,
      }));

      setFormData({ ...formData, prompt: prompt.replace(/["]+/g, ""), theme_list: allThemeOptions });
    },

    onError: (error) => {
      errorToast({ message: "Something went wrong. Please try again.", error });
    },
  });

  const themeOptions =
    generateParametersMutation.data?.theme_list.themes.map((theme) => ({
      label: theme.themeForDisplay,
      value: theme.themeId,
    })) || [];

  const submitImageGenerationRequest = useMutation({
    mutationFn: async (params: Parameters<typeof apiClient.submitImageGenerationRequest>[0]) => {
      return await apiClient.submitImageGenerationRequest(params);
    },

    onSuccess: () => {
      successToast({
        message: `Successfully created image generation request for ASIN ${asin}`,
      });
    },

    onError: (error) => {
      errorToast({
        message: "Something went wrong while creating a new image generation request. Please try again.",
        error,
      });
    },
  });

  return (
    <>
      <Modal
        isOpen={showModal}
        onOpenChange={setShowModal}
        title={`Generate Assets for ASIN ${asin}`}
        className={css({
          width: { base: "90%", lg: "60%" },
        })}
      >
        <VStack gap="large" alignItems="stretch" paddingX="xlarge" overflowY="auto">
          {generateParametersMutation.isPending ?
            <Loading height={"500px"} />
          : <form className={css({ display: "contents" })} onSubmit={(e) => e.preventDefault()}>
              <p
                className={css({
                  margin: 0,
                  fontSize: "medium",
                  textAlign: "center",
                })}
              >
                {initialFormData ?
                  "Fields have been pre-populated with the selected image generation request parameters. You can make updates using the form below, and once you are happy with the updated prompt and theme list you can submit a new request below."
                : "Both fields have been pre-populated and can now be manually refined. Once you are happy with the prompt and theme list, submit your request below."
                }
              </p>
              <TextField
                label={
                  <>
                    Name <span className={css({ fontWeight: "normal" })}>(optional)</span>
                  </>
                }
                placeholder={"Enter a name"}
                value={formData.name}
                onChange={(value) => {
                  setFormData({ ...formData, name: value });
                }}
              />
              <TextAreaField
                label="Prompt"
                placeholder={"Enter a prompt"}
                characterCount={{
                  show: true,
                  max: 300,
                }}
                value={formData.prompt}
                onChange={(value) => {
                  setFormData({ ...formData, prompt: value });
                }}
              />

              <TextAreaField
                label={
                  <Wrap justify={"space-between"} alignItems={"center"} width="100%">
                    <div>
                      Additional Context <span className={css({ fontWeight: "normal" })}>(optional)</span>
                    </div>
                    <Button
                      size="small"
                      onPress={() => {
                        generateParametersMutation.mutate({
                          product: productId,
                          additional_context: formData.additional_context,
                        });
                      }}
                    >
                      Regenerate Prompt
                    </Button>
                  </Wrap>
                }
                placeholder={"Enter additional context"}
                characterCount={{
                  show: true,
                  max: 300,
                }}
                value={formData.additional_context}
                onChange={(value) => {
                  setFormData({ ...formData, additional_context: value });
                }}
              />

              <Form.Group className="py-4">
                <div className="w-100 d-flex justify-content-between">
                  <Label>Themes</Label>
                  <div className="d-flex gap-3">
                    <small
                      style={{ cursor: "pointer" }}
                      onClick={() => {
                        setFormData({ ...formData, theme_list: themeOptions });
                      }}
                    >
                      Select All
                    </small>
                    <small
                      style={{ cursor: "pointer" }}
                      onClick={() => {
                        setFormData({ ...formData, theme_list: [] });
                      }}
                    >
                      Deselect All
                    </small>
                  </div>
                </div>
                <Select
                  value={formData.theme_list}
                  isMulti
                  isClearable={false}
                  inputValue={themeSearch}
                  name="colors"
                  options={themeOptions}
                  classNamePrefix="image-gen-theme-select"
                  closeMenuOnSelect={false}
                  onInputChange={(value, action) => {
                    if (action.action === "input-change") {
                      setThemeSearch(value);
                    }
                    if (action.action === "input-blur") {
                      setThemeSearch("");
                    }
                  }}
                  onChange={(selections) => {
                    setFormData({ ...formData, theme_list: [...selections] });
                  }}
                  styles={{
                    control: (base) => ({
                      ...base,
                      boxShadow: "none",
                      borderColor: "#DDD",
                      overflowY: "auto",
                    }),
                  }}
                />
              </Form.Group>
              <div className="text-center">
                {formData.prompt.length < 10 && (
                  <small className="text-danger d-block pt-3">Prompt must be at least 10 characters in length</small>
                )}
                {formData.prompt.length > 300 && (
                  <small className="text-danger d-block pt-3">Prompt must be 300 characters or less in length</small>
                )}
                {formData.additional_context.length > 300 && (
                  <small className="text-danger d-block pt-3">
                    Additional context must be 300 characters or less in length
                  </small>
                )}
                {formData.theme_list.length < 1 && (
                  <small className="text-danger d-block pt-3">At least one theme is required.</small>
                )}
              </div>
              <Row className="d-flex justify-content-center gap-5 pt-5 pb-3">
                <Button
                  variant="secondary"
                  onPress={() => {
                    setFormData(DEFAULT_FORM_DATA);
                    setShowModal(false);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onPress={() => {
                    submitImageGenerationRequest.mutate({
                      name: formData.name,
                      prompt: formData.prompt,
                      gvproduct: productId,
                      theme_list: formData.theme_list.map((theme) => ({
                        themeForDisplay: theme.label,
                        themeId: theme.value,
                      })),
                    });
                    setFormData(DEFAULT_FORM_DATA);
                    setShowModal(false);
                  }}
                  isDisabled={
                    formData.prompt.length < 10 ||
                    formData.prompt.length > 300 ||
                    formData.additional_context.length > 300 ||
                    !formData.theme_list.length
                  }
                >
                  Generate Images
                </Button>
              </Row>
            </form>
          }
        </VStack>
      </Modal>
      <Center>
        {overrideModalButtonText ?
          <Button
            variant={"contentOnly"}
            onPress={() => {
              setShowModal(true);
            }}
          >
            {overrideModalButtonText}
          </Button>
        : <Button
            onPress={() => {
              generateParametersMutation.mutate({
                product: productId,
                additional_context: "",
              });
              setShowModal(true);
            }}
          >
            Generate AI Images
          </Button>
        }
      </Center>
    </>
  );
};

export default ImageGenerationDialogue;
